import React from 'react';
import { lazyGettext } from '@eb/lazy-gettext';

import { Link } from '@eb/eds-link';

import { LogoEBrand } from '@eb/eds-iconography';
import { User } from '@eb/eds-iconography';
import { Mail } from '@eb/eds-iconography';
import { Key } from '@eb/eds-iconography';

import LinkFacebookAccountSvg from '../components/illustrations/LinkFacebookAccount';
import LinkAppleAccountSvg from '../components/illustrations/LinkAppleAccount';

import {
    ACCOUNT_STATUS,
    LOG_IN_PATH,
    SIGN_UP_PATH,
    CHECKOUT_PATH,
    SOCIAL_SIGN_UP_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    RESET_PASSWORD_PATH,
    SET_PASSWORD_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LINK_LOGIN_PATH,
    RESET_PASSWORD_STATUS_PATH,
    SOFT_ACCOUNT_STATUS_PATH,
    TRIGGER_RESET_PASSWORD,
} from './index';

export const PASSWORD_TOKEN_EXPIRED_MESSAGE = lazyGettext(
    'Your password reset token has expired. Please try again.',
);

export const LINKED_ACCOUNTS_SUCCESS_TITLE = lazyGettext(
    'Your account is now linked!',
);

export const LINKED_ACCOUNTS_SUCCESS_MESSAGE = lazyGettext(
    'You can now log into Eventbrite using your Social account. You may unlink your account any time under your Linked Accounts settings.',
);

export const EMAILS_MISMATCHES = lazyGettext(
    "Email address doesn't match. Please try again",
);

const ACCOUNT_STATUS_INFO = {
    headerText: lazyGettext('Sign up or log in'),
    subtitleText: '',
    iconType: <LogoEBrand />,
    iconColor: 'ui-orange',
    haloColor: 'ui-orange',
    buttonCTA: lazyGettext('Get Started'),
};

const CHECKOUT_LOG_IN_INFO = {
    headerText: lazyGettext('Log in to Eventbrite'),
    subtitleText: lazyGettext('Log in for a faster checkout experience'),
    iconType: <LogoEBrand />,
    iconColor: 'white',
    haloColor: 'ui-orange',
    buttonCTA: lazyGettext('Log in'),
};

const LOG_IN_FORM_INFO = {
    headerText: lazyGettext('Welcome back'),
    iconType: <User />,
    subtitleText: lazyGettext('Please enter your password to log in.'),
    buttonCTA: lazyGettext('Log In'),
};

const LINK_ACCOUNTS_LOG_IN_FORM_INFO = {
    headerText: lazyGettext('Link accounts'),
    iconType: <LinkFacebookAccountSvg />,
    iconColor: null,
    iconSize: null,
    subtitleText: null,
    buttonCTA: lazyGettext('Link your accounts'),
};
const LINK_ACCOUNTS_LOG_IN_FORM_INFO_APPLE = {
    headerText: lazyGettext('Link accounts'),
    iconType: <LinkAppleAccountSvg />,
    iconColor: null,
    iconSize: null,
    subtitleText: null,
    buttonCTA: lazyGettext('Link your accounts'),
};

const SIGN_UP_FORM_INFO = {
    headerText: lazyGettext('Welcome'),
    iconType: <User />,
    subtitleText: lazyGettext('Create an account.'),
    buttonCTA: lazyGettext('Sign Up'),
};

const SOCIAL_SIGN_UP_FORM_INFO = {
    headerText: lazyGettext('Welcome'),
    iconType: <User />,
    subtitleText: lazyGettext(
        'Please confirm your email and name to continue with Facebook.',
    ),
    buttonCTA: lazyGettext('Sign Up'),
};

const SOFT_ACCOUNT_STATUS_INFO = {
    headerText: lazyGettext('Check your email'),
    subtitleText: lazyGettext(
        "Looks like you're already in our system! Check your email to create a new password.",
    ),
    iconType: <Mail />,
};

export const SOFT_ACCOUNT_SEND_EMAIL_FAILED =
    'There has been an error. Please try again later.';

const SET_LINKED_ACCOUNTS_PASSWORD_INFO = {
    headerText: lazyGettext('Welcome back'),
    iconType: <User />,
    subtitleText: null,
    buttonCTA: lazyGettext('Create Password'),
};

const RESET_PASSWORD_STATUS_INFO = {
    headerText: lazyGettext('Check your email'),
    subtitleText: lazyGettext(
        'We sent you an email with instructions to reset your password.',
    ),
    iconType: <Mail />,
};

const RESET_PASSWORD_INFO = {
    headerText: lazyGettext('Reset your password'),
    iconType: <Key />,
    buttonCTA: lazyGettext('Reset password'),
    subtitleText: lazyGettext('Enter your new password below.'),
};

const TRIGGER_RESET_PASSWORD_INFO = {
    headerText: lazyGettext('Reset your password'),
    iconType: <Key />,
    buttonCTA: lazyGettext('Send email'),
    subtitleText: lazyGettext(
        "Enter your email address and we'll send you a link to reset your password.",
    ),
};

const SET_PASSWORD_INFO = {
    headerText: lazyGettext('Create an account'),
    iconType: <User />,
    buttonCTA: lazyGettext('Sign up'),
    subtitleText: lazyGettext(
        "You're almost there! Enter a password to complete your account.",
    ),
};

const SOCIAL_LOG_IN_INFO = {
    headerText: lazyGettext('You look familiar'),
    buttonCTA: lazyGettext('Link account'),
    subtitleText: lazyGettext(
        'An Eventbrite account with your email already exists. Enter your password to continue.',
    ),
};

export const EXISTING_ACCOUNT_INFO = {
    headerText: lazyGettext('Used Eventbrite before?'),
    buttonCTA: lazyGettext('Link account'),
    subtitleText: lazyGettext(
        'If you have an existing Eventbrite account, enter your email and password to see all your tickets in one place.',
    ),
    yesButtonText: lazyGettext('Yes, I have an account'),
    noButtonText: lazyGettext("No, I'm new here"),
};

/*Should be extended to include every form/notification screen etc.*/
export const PAGE_HEADER_INFO_MAP = {
    [`${ACCOUNT_STATUS}Form`]: ACCOUNT_STATUS_INFO,
    [`${LOG_IN_PATH}Form`]: LOG_IN_FORM_INFO,
    [`${LINK_ACCOUNTS_LOG_IN_PATH}Form`]: LINK_ACCOUNTS_LOG_IN_FORM_INFO,
    [`${LINK_ACCOUNTS_LOG_IN_PATH}AppleForm`]: LINK_ACCOUNTS_LOG_IN_FORM_INFO_APPLE,
    [`${SIGN_UP_PATH}Form`]: SIGN_UP_FORM_INFO,
    [`${SOCIAL_SIGN_UP_PATH}Form`]: SOCIAL_SIGN_UP_FORM_INFO,
    [`${SET_LINKED_SOFT_ACCOUNTS_PATH}Form`]: SET_LINKED_ACCOUNTS_PASSWORD_INFO,
    [RESET_PASSWORD_STATUS_PATH]: RESET_PASSWORD_STATUS_INFO,
    [SOFT_ACCOUNT_STATUS_PATH]: SOFT_ACCOUNT_STATUS_INFO,
    [TRIGGER_RESET_PASSWORD]: TRIGGER_RESET_PASSWORD_INFO,
    [RESET_PASSWORD_PATH]: RESET_PASSWORD_INFO,
    [SET_PASSWORD_PATH]: SET_PASSWORD_INFO,
    [`${SOCIAL_LINK_LOGIN_PATH}Form`]: SOCIAL_LOG_IN_INFO,
    [`${SOCIAL_LOG_IN_PATH}Form`]: EXISTING_ACCOUNT_INFO,
};

export const PAGE_HEADER_INFO_MAP_CHECKOUT = {
    [`${LOG_IN_PATH}Form`]: CHECKOUT_LOG_IN_INFO,
    [`${CHECKOUT_PATH}Form`]: CHECKOUT_LOG_IN_INFO,
    [`${SOCIAL_LOG_IN_PATH}Form`]: CHECKOUT_LOG_IN_INFO,
};

export const EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR_MESSAGE = (
    <div>
        {lazyGettext(
            'Your email already has an Eventbrite account. Log in and ',
        )}
        <Link
            to="/support/articleredirect?anum=41538"
            target="_blank"
            rel="noopener noreferrer"
        >
            {lazyGettext('link your account to Facebook')}
        </Link>
    </div>
);

export const EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE = (
    <div>
        {lazyGettext(
            "This email has been used in Eventbrite before. To login, you'll need to create a password for this account",
        )}
    </div>
);

export const LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML = (
    <div>
        {lazyGettext(
            "This email address is already registered with an Eventbrite account. We'll send you a magic link to connect your accounts.",
        )}
    </div>
);

export const ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Facebook. Use the Facebook button to log in.',
);

export const ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Apple. Use the Apple button to log in.',
);

export const ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Google. Use the Google button to log in.',
);

export const ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Apple and Facebook. Use the Apple or Facebook button to log in.',
);
export const ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Apple and Google. Use the Apple or Google button to log in.',
);
export const ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Google and Facebook. Use the Google or Facebook button to log in.',
);
export const ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE = lazyGettext(
    'Your account is connected to Apple, Google and Facebook. Use the Apple, Google or Facebook button to log in.',
);
export const ACCOUNT_CONNECTED_TO_SOCIAL_ERROR_MESSAGE = lazyGettext(
    'There is a social account associated for the provided email address. Please use the Social Button to log in.',
);

export const EMAIL_PERMISSION_DENIED_ERROR_MESSAGE = (
    <div>
        {lazyGettext(
            'The email address associated with your Facebook account could not be retrieved. Try again and grant access to email permissions or add an email to your Facebook account. ',
        )}
        <Link
            to=" https://www.facebook.com/help/162801153783275"
            target="_blank"
            rel="noopener noreferrer"
        >
            {lazyGettext('Learn how')}
        </Link>
    </div>
);

export const USER_ALREADY_HAS_EXTERNAL_PROVIDER_MESSAGE = lazyGettext(
    'The user has already linked a Social account. To continue, sign in with your Eventbrite account and unlink it manually.',
);
export const TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE = lazyGettext(
    'Unexpected error. That was weird. Try again.',
);

export const UNKNOWN_ERROR_MESSAGE = lazyGettext(
    'Whoops! Someone must have pulled a plug somewhere... try again.',
);

export const UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE = lazyGettext(
    'Whoops! There was an error with your login attempt. Please try again later or choose a different login method.',
);

export const LOCKED_ACCOUNT_ERROR_MESSAGE = lazyGettext(
    'Your Account has been locked.',
);

export const SUSPENDED_ACCOUNT_ERROR_MESSAGE = lazyGettext(
    'Your Account has been suspended.',
);

export const MUST_CHANGE_PASSWORD_ERROR_MESSAGE = lazyGettext(
    'This user’s password must change before logging in.',
);

export const EMAIL_UNEXISTENT_ERROR_MESSAGE = lazyGettext(
    'We couldn’t find that email address. Please try a different one.',
);

export const EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE = lazyGettext(
    'We couldn’t find an Eventbrite account associated with this address. Try again or continue as guest.',
);

export const EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE = lazyGettext(
    'The account associated with your email has no set password. Continue as a guest to complete your order.',
);

export const SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE = lazyGettext(
    'Your account is already linked with another account. Please try a different one.',
);

export const ONLY_SOCIAL_LOGIN_ERROR_MESSAGE = lazyGettext(
    'If you’ve logged in through Facebook, you can link your Apple account in account settings',
);

export const FACEBOOK_ACCOUNT_NOT_LOGGED_IN_ERROR = lazyGettext(
    'We couldn’t verify your account. Please continue as guest to complete your order.',
);

export const NOT_EMAIL_ASSOCIATED = lazyGettext(
    'There is no account associated with the email.',
);

export const SIGNUP_EMAIL_EXISTS = lazyGettext(
    'There is an account associated with the email.',
);

export const LOGIN_SOFT_ACCOUNT = lazyGettext(
    'We sent you an email to {email} with intructions to set your password.',
);

export const CREATE_ACCOUNT = lazyGettext('Create account');

export const PASSWORD_INCORRECT = lazyGettext('The password is not correct.');

export const TOS_CREATE_ACCOUNT_MUST_BE_ACCEPTED = lazyGettext(
    'To create account you have to accept our Terms and conditions. To continue press Create account',
);

export const TOS_SET_PASSWORD_MUST_BE_ACCEPTED = lazyGettext(
    'To set a password you have to accept our Terms and conditions. To continue press Set Password',
);

export const TOS_SOCIAL_MUST_BE_ACCEPTED = lazyGettext(
    'To continue you have to accept our Terms and conditions.',
);
